import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import LayoutContainer from "../../LayoutContainer";
import styles from "./managementDashboardDetails.module.css";
import { useHistory, useLocation } from "react-router-dom";
import SimpleBackIcon from "../../../assets/simpleBackIcon.svg";
import DashboardFilters from "../Helpers/Filters/DashboardFilters";
import { useDashboardContext } from "../../../contexts/dashboard-context/DashboardContext";
import DatePickerModal from "../../DatePicker";
import { AuthContext } from "../../../contexts/auth-context";
import { useAsyncFn } from "../../../hooks/use-async-fn";
import DetailsPageChartCard from "../Helpers/Charts/DetailsPageChartCard";
import { formatCount } from "../index";
import DataTable, { DataTableRow } from "../Helpers/DataTable";
import {
  MANAGEMENT_DASHBOARD_BASE,
  buildDetailsApiQueryParams,
  getDetailsPageTableColumns,
  getManagementDetailsConfig,
  initialFilterValuesForConfig,
  withAll,
} from "./managementDashboardDetailsConfig";
import { formatAdserverType } from "../../../utils";
import DrillDownDrawer, {
  DrillDownTarget,
  DrillEndpoint,
  MonthOption,
} from "../Helpers/DrillDownDrawer";

type DetailApiRow = {
  month_name: string;
  year: number;
  event_type: string;
  total_count?: number;
  query_count?: number;
  federation_id?: string;
  federation_name?: string;
  organization_id?: string;
  organization_name?: string;
  destination_adserver_type?: string;
};

type DetailDayApiRow = {
  date: string;
  event_type: string;
  total_count?: number;
  query_count?: number;
  federation_id?: string;
  federation_name?: string;
  organization_id?: string;
  organization_name?: string;
  destination_adserver_type?: string;
};

type ChartPoint = {
  date: string;
  query: number;
  engaged: number;
  audienceTransferred: number;
};

type FederationMetricKey = "total_count" | "query_count";

const toLocalDateOnly = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const defaultDateRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - 3);
  return {
    start: toLocalDateOnly(start),
    end: toLocalDateOnly(end),
  };
};

type ManagementDashboardLocationState = {
  dateRange?: { start?: string; end?: string };
  selectedEventType?: string;
  federationIds?: string[];
  /** Selected application IDs from the main dashboard — used to restore state on Back. */
  applicationIds?: string[];
  /** Explicitly selected federation IDs — used to restore state on Back. */
  selectedFedIds?: string[];
  /** Organizations linked to the selected application — used to build the
   *  organization dropdown options and scope API calls on the org details page.
   *  Full objects are carried so orgs with no activity still appear in the dropdown. */
  organizations?: { id: string; name: string }[];
  /** Ad-server types (destination_adserver_type values) linked to the selected
   *  application — used to scope the adserver type dropdown on the details page. */
  adserverTypes?: string[];
};

const getDateRangeFromLocationState = (
  state: ManagementDashboardLocationState | undefined,
) => {
  const start = state?.dateRange?.start;
  const end = state?.dateRange?.end;
  if (start && end) {
    return { start, end };
  }
  return null;
};

const getInitialFilterValues = (
  pageConfig: ReturnType<typeof getManagementDetailsConfig>,
  state: ManagementDashboardLocationState | undefined,
) => {
  const next = initialFilterValuesForConfig(pageConfig);
  const hasEventTypeFilter = pageConfig.filters.some(
    (filter) => filter.id === "event_type",
  );
  if (hasEventTypeFilter && state?.selectedEventType) {
    next.event_type = state.selectedEventType;
  }
  return next;
};

const formatToDateOnly = (value: string | Date) => {
  if (typeof value === "string") {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  return toLocalDateOnly(d);
};

const toDatetimeLocal = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Prefer a non-empty name; else id; else "-". */
const resolveEntityDisplayName = (
  name: string | undefined | null,
  id: string | undefined | null,
): string => {
  const trimmed = typeof name === "string" ? name.trim() : "";
  return trimmed || id || "-";
};

const buildChartDataForMetric = (
  data: DetailApiRow[] | undefined,
  metricKey: FederationMetricKey,
): ChartPoint[] | null => {
  if (!data?.length) {
    return null;
  }
  return data.reduce<ChartPoint[]>((acc, item) => {
    const chartPoint = acc.find(
      (point) =>
        point.date === `${item.month_name} ${String(item.year).slice(-2)}`,
    );

    const key =
      item.event_type === "query"
        ? "query"
        : item.event_type === "marked_engaged"
          ? "engaged"
          : item.event_type === "audience_transferred"
            ? "audienceTransferred"
            : null;

    if (!key) {
      return acc;
    }

    const metricValue = item[metricKey];

    if (chartPoint) {
      chartPoint[key] = (chartPoint[key] ?? 0) + (metricValue ?? 0);
    } else {
      acc.push({
        date: `${item.month_name} ${String(item.year).slice(-2)}`,
        query: key === "query" ? (metricValue ?? 0) : 0,
        engaged: key === "engaged" ? (metricValue ?? 0) : 0,
        audienceTransferred:
          key === "audienceTransferred" ? (metricValue ?? 0) : 0,
      });
    }

    return acc;
  }, []);
};

const ManagementDashboardDetails: React.FC = () => {
  const location = useLocation<ManagementDashboardLocationState | undefined>();
  const { pathname } = location;
  const pageConfig = useMemo(
    () => getManagementDetailsConfig(pathname),
    [pathname],
  );

  const [filterValues, setFilterValues] = useState<Record<string, string>>(() =>
    getInitialFilterValues(pageConfig, location.state),
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    return getDateRangeFromLocationState(location.state) ?? defaultDateRange();
  });
  const history = useHistory();
  const { dashboardData } = useDashboardContext();
  const {
    getFederationDetails,
    getOrganizationDetails,
    getAdserverDetails,
    tableGetFederationMonthLevelCount,
    tableGetOrganizationMonthLevelCount,
    tableGetAdserverMonthLevelCount,
    tableGetFederationDayLevelCount,
    tableGetOrganizationDayLevelCount,
    tableGetAdserverDayLevelCount,
  } = useContext(AuthContext);

  // Failsafe: table APIs can return blank names — fall back to id, then "-".
  const resolveRowEntityId = useCallback(
    (item: DetailApiRow | DetailDayApiRow) => {
      if (pageConfig.variant === "organization") {
        return resolveEntityDisplayName(
          item.organization_name,
          item.organization_id,
        );
      }
      if (pageConfig.variant === "federation") {
        return resolveEntityDisplayName(
          item.federation_name,
          item.federation_id,
        );
      }
      return item.destination_adserver_type?.trim() || "-";
    },
    [pageConfig.variant],
  );

  const [drillTarget, setDrillTarget] = useState<DrillDownTarget | null>(null);

  const deferFn =
    pageConfig.variant === "organization"
      ? getOrganizationDetails
      : pageConfig.variant === "adserver"
        ? getAdserverDetails
        : getFederationDetails;

  const { run } = useAsyncFn({ deferFn });

  const [detailsResponse, setDetailsResponse] = useState<{
    data?: DetailApiRow[];
  } | null>(null);
  const [totalCountChartData, setTotalCountChartData] = useState<
    ChartPoint[] | null
  >(null);
  const [queryCountChartData, setQueryCountChartData] = useState<
    ChartPoint[] | null
  >(null);
  const [tableData, setTableData] = useState<DataTableRow[] | null>(null);
  const [selectedTableMonth, setSelectedTableMonth] = useState<string | null>(
    null,
  );
  const [tableDayRows, setTableDayRows] = useState<DataTableRow[]>([]);
  const [tableDayLoading, setTableDayLoading] = useState(false);

  useEffect(() => {
    setFilterValues((prev) => {
      const next = getInitialFilterValues(pageConfig, location.state);
      if (JSON.stringify(prev) === JSON.stringify(next)) {
        return prev;
      }
      return next;
    });
  }, [pageConfig, location.state]);

  const apiQueryParams = useMemo(
    () => buildDetailsApiQueryParams(pageConfig.filters, filterValues),
    [pageConfig.filters, filterValues],
  );

  // Federations selected on the main dashboard (or all federations linked to the
  // selected application), carried over via navigation state so every details
  // page stays scoped to the same application context.
  // For the federation variant the in-page federation filter provides further
  // refinement; we only add the top-level scope when no specific federation_id
  // has already been chosen via that in-page filter (to avoid redundant params).
  const topFederationIds = location.state?.federationIds;
  // Organizations linked to the selected application — used to build the org
  // dropdown options and scope API calls on the org details page.
  const topOrganizations = location.state?.organizations;
  // Ad-server types linked to the selected application — used to scope the
  // adserver type dropdown on the adserver details page.
  const topAdserverTypes = location.state?.adserverTypes;
  const requestParams = useMemo(
    () => ({
      ...apiQueryParams,
      ...(topFederationIds?.length &&
        (pageConfig.variant !== "federation" || !apiQueryParams.federation_id)
        ? { federation_ids: topFederationIds }
        : {}),
      ...(topOrganizations?.length &&
        pageConfig.variant === "organization" && !apiQueryParams.organization_id
        ? { organization_ids: topOrganizations.map((o) => o.id) }
        : {}),
    }),
    [apiQueryParams, topFederationIds, topOrganizations, pageConfig.variant],
  );

  const fetchDetails = useCallback(
    async (
      start: string,
      end: string,
      filters: Record<string, string | number | string[]>,
    ) => {
      setTotalCountChartData(null);
      setQueryCountChartData(null);
      try {
        const response = await run({
          start_date: start,
          end_date: end,
          ...filters,
        });
        setDetailsResponse(response);
      } catch (error) {
        console.error("Failed to fetch management details", error);
      }
    },
    [run],
  );

  useEffect(() => {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    fetchDetails(dateRange.start, dateRange.end, requestParams);
  }, [dateRange.start, dateRange.end, requestParams, fetchDetails]);

  const handleDateApply = (start: string, end: string) => {
    setDateRange({
      start: formatToDateOnly(start),
      end: formatToDateOnly(end),
    });
  };

  const formatDateLabel = () => {
    if (!dateRange.start || !dateRange.end) return "Select date range";
    const fmt = (dateOnly: string) => {
      const [y, m, day] = dateOnly.split("-");
      return `${day}/${m}/${y}`;
    };
    return `${fmt(dateRange.start)} - ${fmt(dateRange.end)}`;
  };

  const { pickerInitialStart, pickerInitialEnd } = useMemo(() => {
    if (dateRange.start && dateRange.end) {
      return {
        pickerInitialStart: `${dateRange.start}T00:00`,
        pickerInitialEnd: `${dateRange.end}T23:59`,
      };
    }
    const end = new Date();
    const start = new Date(end);
    start.setMonth(start.getMonth() - 3);
    start.setHours(0, 0, 0, 0);
    return {
      pickerInitialStart: toDatetimeLocal(start),
      pickerInitialEnd: toDatetimeLocal(end),
    };
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    const rows = detailsResponse?.data;
    if (rows && rows.length) {
      setTotalCountChartData(buildChartDataForMetric(rows, "total_count"));
      setQueryCountChartData(buildChartDataForMetric(rows, "query_count"));
    } else if (detailsResponse) {
      setTotalCountChartData([]);
      setQueryCountChartData([]);
    }
  }, [detailsResponse]);

  const tableMonthOptions = useMemo(
    () =>
      Array.from(
        new Set((tableData ?? []).map((r) => String(r.timePeriodMonth))),
      ),
    [tableData],
  );

  useEffect(() => {
    if (!dateRange.start || !dateRange.end) return;
    const tableMonthApiFn =
      pageConfig.variant === "organization"
        ? tableGetOrganizationMonthLevelCount
        : pageConfig.variant === "adserver"
          ? tableGetAdserverMonthLevelCount
          : tableGetFederationMonthLevelCount;
    const controller = new AbortController();
    setTableData(null);
    tableMonthApiFn({
      args: {
        start_date: dateRange.start,
        end_date: dateRange.end,
        ...requestParams,
      },
      signal: controller.signal,
    })
      .then((response: { data?: DetailApiRow[] }) => {
        if (!controller.signal.aborted) {
          const rows = response?.data ?? [];
          setTableData(
            rows.map((item, index) => ({
              id: `${item.federation_id ?? item.organization_id ?? item.destination_adserver_type ?? "row"}-${item.month_name} ${item.year}-${item.event_type}-${index}`,
              entityId: resolveRowEntityId(item),
              adserverType: item.destination_adserver_type?.trim() || "-",
              timePeriodMonth: `${item.month_name} ${item.year}`,
              eventType: item.event_type ?? "-",
              totalCount: item.total_count ?? 0,
              queryCount: item.query_count ?? 0,
            })),
          );
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setTableData([]);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange.start,
    dateRange.end,
    requestParams,
    pageConfig.variant,
    resolveRowEntityId,
  ]);

  useEffect(() => {
    if (!selectedTableMonth) {
      setTableDayRows([]);
      setTableDayLoading(false);
      return;
    }
    const parsed = new Date(`${selectedTableMonth} 1 00:00:00`);
    if (Number.isNaN(parsed.getTime())) return;
    const { startDate, endDate } = buildMonthRange(
      parsed.getFullYear(),
      parsed.getMonth(),
    );
    const dayApiFn =
      pageConfig.variant === "organization"
        ? tableGetOrganizationDayLevelCount
        : pageConfig.variant === "adserver"
          ? tableGetAdserverDayLevelCount
          : tableGetFederationDayLevelCount;
    const controller = new AbortController();
    setTableDayLoading(true);
    setTableDayRows([]);
    dayApiFn({
      args: {
        start_date: startDate,
        end_date: endDate,
        ...requestParams,
      },
      signal: controller.signal,
    })
      .then((res: { data?: DetailDayApiRow[] }) => {
        if (!controller.signal.aborted) {
          const data = res?.data ?? [];
          setTableDayRows(
            data.map((item, index) => ({
              id: `day-${item.federation_id ?? item.organization_id ?? item.destination_adserver_type ?? "row"}-${item.date}-${item.event_type}-${index}`,
              entityId: resolveRowEntityId(item),
              adserverType: item.destination_adserver_type?.trim() || "-",
              date: item.date
                ? (() => {
                    const d = new Date(`${item.date}T00:00:00`);
                    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
                  })()
                : "-",
              eventType: item.event_type ?? "-",
              totalCount: item.total_count ?? 0,
              queryCount: item.query_count ?? 0,
            })),
          );
          setTableDayLoading(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setTableDayLoading(false);
      });
    return () => controller.abort();
  }, [
    selectedTableMonth,
    pageConfig.variant,
    requestParams,
    tableGetFederationDayLevelCount,
    tableGetOrganizationDayLevelCount,
    tableGetAdserverDayLevelCount,
    resolveRowEntityId,
  ]);

  const tableColumns = useMemo(
    () =>
      getDetailsPageTableColumns(
        pageConfig.variant,
        selectedTableMonth ? "day" : "month",
      ),
    [pageConfig.variant, selectedTableMonth],
  );

  const displayTableRows = selectedTableMonth
    ? tableDayRows
    : (tableData ?? []);

  const isTotalCountChartReady = Array.isArray(totalCountChartData);
  const isQueryCountChartReady = Array.isArray(queryCountChartData);
  const isTableDataReady = Array.isArray(tableData);

  const tableLoading = selectedTableMonth ? tableDayLoading : !isTableDataReady;

  const setFilter = (filterId: string) => (value: string) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: value === "ALL" ? "" : value,
    }));
  };

  const clearAllFilters = () => {
    setFilterValues(initialFilterValuesForConfig(pageConfig));
    setSelectedTableMonth(null);
    const end = new Date();
    const start = new Date(end);
    start.setMonth(start.getMonth() - 3);
    setDateRange({
      start: toLocalDateOnly(start),
      end: toLocalDateOnly(end),
    });
  };
  // Build start/end ISO dates for a given month index + year.
  // Uses local date parts (not toISOString) to avoid UTC timezone shift.
  const buildMonthRange = (
    year: number,
    monthIdx: number,
  ): { startDate: string; endDate: string } => {
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    return {
      startDate: fmt(new Date(year, monthIdx, 1)),
      endDate: fmt(new Date(year, monthIdx + 1, 0)),
    };
  };
  // Derive the list of months covered by the current date range
  const availableMonths = (): MonthOption[] => {
    if (!dateRange.start || !dateRange.end) return [];
    const months: MonthOption[] = [];
    // Parse as local time (append T00:00:00) to avoid UTC date shift
    const start = new Date(dateRange.start + "T00:00:00");
    const end = new Date(dateRange.end + "T00:00:00");
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= end) {
      const { startDate, endDate } = buildMonthRange(
        cur.getFullYear(),
        cur.getMonth(),
      );
      months.push({
        label: cur.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
        startDate,
        endDate,
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  };

  const openDrill = (
    cardTitle: string,
    eventType?: string | null,
    monthLabelStr?: string,
    endpoint?: DrillEndpoint,
    entityParams?: Record<string, string | number>,
    chart?: Pick<
      DrillDownTarget,
      "chartLinesByEventType" | "dayLevelChartMetric" | "allowedDimensionKeys"
    >,
  ) => {
    const months = availableMonths();
    if (monthLabelStr) {
      const fallbackStartDate = months[months.length - 1]?.startDate;
      let parsedStartDate: string | undefined;

      if (/^\d{4}-\d{2}-\d{2}$/.test(monthLabelStr)) {
        const [yearStr, monthStr] = monthLabelStr.split("-");
        const year = Number(yearStr);
        const monthIdx = Number(monthStr) - 1;
        if (!Number.isNaN(year) && monthIdx >= 0 && monthIdx <= 11) {
          parsedStartDate = buildMonthRange(year, monthIdx).startDate;
        }
      } else {
        // Handle chart labels like "Jan 24" or "Jan 2024".
        const monthMatch = monthLabelStr.match(
          /^([A-Za-z]{3})\s+(\d{2}|\d{4})$/,
        );
        if (monthMatch) {
          const monthMap: Record<string, number> = {
            Jan: 0,
            Feb: 1,
            Mar: 2,
            Apr: 3,
            May: 4,
            Jun: 5,
            Jul: 6,
            Aug: 7,
            Sep: 8,
            Oct: 9,
            Nov: 10,
            Dec: 11,
          };
          const monthIdx = monthMap[monthMatch[1]];
          const rawYear = Number(monthMatch[2]);
          const year = monthMatch[2].length === 2 ? 2000 + rawYear : rawYear;
          if (monthIdx !== undefined && !Number.isNaN(year)) {
            parsedStartDate = buildMonthRange(year, monthIdx).startDate;
          }
        }
      }
      const startDate = parsedStartDate ?? fallbackStartDate;
      setDrillTarget({
        cardTitle,
        availableMonths: months,
        defaultMonthStartDate: startDate,
        eventType: eventType ?? undefined,
        endpoint,
        entityParams,
        ...chart,
      });
    } else {
      setDrillTarget({
        cardTitle,
        availableMonths: months,
        eventType: eventType ?? undefined,
        endpoint,
        entityParams,
        ...chart,
      });
    }
  };

  const LINE_KEY_TO_EVENT_TYPE: Record<string, string> = {
    query: "query",
    engaged: "marked_engaged",
    audienceTransferred: "audience_transferred",
  };

  const toAllowedEventTypes = (visibleKeys: string[]): string[] | undefined => {
    const mapped = visibleKeys.map((k) => LINE_KEY_TO_EVENT_TYPE[k] ?? k);
    return mapped.length > 0 && mapped.length < 3 ? mapped : undefined;
  };

  return (
    <LayoutContainer>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <button
            className={styles.backBtn}
            onClick={() =>
              history.push({
                pathname: MANAGEMENT_DASHBOARD_BASE,
                state: {
                  ...(location.state?.applicationIds?.length
                    ? { applicationIds: location.state.applicationIds }
                    : {}),
                  ...(location.state?.selectedFedIds !== undefined
                    ? { selectedFedIds: location.state.selectedFedIds }
                    : {}),
                  ...(location.state?.organizations?.length
                    ? { organizations: location.state.organizations }
                    : {}),
                  ...(location.state?.adserverTypes?.length
                    ? { adserverTypes: location.state.adserverTypes }
                    : {}),
                },
              })
            }
          >
            <img src={SimpleBackIcon} alt="back" />
            <span>Back</span>
          </button>
          <h1 className={styles.pageTitle}>{pageConfig.pageTitle}</h1>
          <div className={styles.pageHeaderSpacer} />
        </div>

        <div className={styles.container}>
          <div className={styles.federationSummary}>
            <h3 className={styles.federationSummaryTitle}>
              {pageConfig.sectionTitle}
            </h3>
            <div className={styles.topBarRight}>
              <button
                className={styles.clearFiltersLink}
                onClick={clearAllFilters}
              >
                Clear filters
              </button>
              <button
                className={styles.dateRangeBtn}
                onClick={() => setDatePickerOpen(true)}
                type="button"
              >
                <span>{formatDateLabel()}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 4H5C3.9 4 3 4.9 3 6V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8ZM9 14H7V12H9V14ZM13 14H11V12H13V14ZM17 14H15V12H17V14ZM9 18H7V16H9V18ZM13 18H11V16H13V18ZM17 18H15V16H17V18Z"
                    fill="var(--Primary-Color-Shade-1)"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.filtersRow}>
            {pageConfig.filters.map((currentFilter) => {
              const baseOptions = currentFilter.getOptions(dashboardData);
              // Scope federation options to app-linked feds; for organizations,
              // build the options directly from the carried org objects so that
              // orgs with no activity in the current date range still appear.
              const options =
                currentFilter.id === "federation_id" && topFederationIds?.length
                  ? baseOptions.filter(
                      (opt) =>
                        opt.value === "ALL" ||
                        topFederationIds.includes(opt.value),
                    )
                  : currentFilter.id === "organization_id" && topOrganizations?.length
                    ? withAll(
                        topOrganizations.map((o) => ({
                          value: o.id,
                          label: o.name,
                        })),
                      )
                    : currentFilter.id === "destination_adserver_type" && topAdserverTypes?.length
                      ? withAll(
                          topAdserverTypes.map((t) => ({
                            value: t,
                            label: formatAdserverType(t),
                          })),
                        )
                      : baseOptions;
              return (
                <DashboardFilters
                  key={currentFilter.id}
                  value={filterValues[currentFilter.id] ?? ""}
                  options={options}
                  onChange={
                    currentFilter.disabled
                      ? () => {}
                      : setFilter(currentFilter.id)
                  }
                  selectName={currentFilter.selectName}
                  disabled={currentFilter.disabled}
                  style={currentFilter.style}
                  preserveOptionCase={currentFilter.preserveOptionCase}
                />
              );
            })}
          </div>

          <div className={styles.chartsGrid}>
            <div className={styles.chartContainer}>
              <DetailsPageChartCard
                title="Event Count"
                xAxisKey="date"
                data={totalCountChartData ?? []}
                yTickFormatter={formatCount}
                isLoading={!isTotalCountChartReady}
                lines={[
                  { key: "query", name: "Query", color: "#0D6AB3" },
                  { key: "engaged", name: "Engaged", color: "#2ACDFF" },
                  {
                    key: "audienceTransferred",
                    name: "Audience Transferred",
                    color: "#F59E0B",
                  },
                ]}
                onMonthClick={(label, visibleKeys) =>
                  openDrill(
                    pageConfig.variant === "federation"
                      ? "Federation wise event of count summary"
                      : pageConfig.variant === "organization"
                        ? "Organization Event Summary"
                        : "Ad Server wise event of count summary",
                    filterValues.event_type ?? "",
                    label,
                    (pageConfig.variant === "federation"
                      ? "federation"
                      : pageConfig.variant === "organization"
                        ? "organization"
                        : "adserver") as DrillEndpoint,
                    apiQueryParams,
                    {
                      chartLinesByEventType: true,
                      dayLevelChartMetric: "total_count",
                      allowedDimensionKeys: toAllowedEventTypes(visibleKeys),
                    },
                  )
                }
              />
            </div>
            <div className={styles.chartContainer}>
              <DetailsPageChartCard
                title="Audience Volume"
                xAxisKey="date"
                data={queryCountChartData ?? []}
                yTickFormatter={formatCount}
                isLoading={!isQueryCountChartReady}
                lines={[
                  { key: "query", name: "Query", color: "#0D6AB3" },
                  { key: "engaged", name: "Engaged", color: "#2ACDFF" },
                  {
                    key: "audienceTransferred",
                    name: "Audience Transferred",
                    color: "#F59E0B",
                  },
                ]}
                onMonthClick={(label, visibleKeys) =>
                  openDrill(
                    pageConfig.variant === "federation"
                      ? "Federation wise audience Count summary"
                      : pageConfig.variant === "organization"
                        ? "Organization wise audience Count summary"
                        : "Ad Server wise audience Count summary",
                    filterValues.event_type ?? "",
                    label,
                    (pageConfig.variant === "federation"
                      ? "federation"
                      : pageConfig.variant === "organization"
                        ? "organization"
                        : "adserver") as DrillEndpoint,
                    apiQueryParams,
                    {
                      chartLinesByEventType: true,
                      dayLevelChartMetric: "query_count",
                      allowedDimensionKeys: toAllowedEventTypes(visibleKeys),
                    },
                  )
                }
              />
            </div>
          </div>
          <div className={styles.tableContainer}>
            <DataTable
              title={pageConfig.tableTitle}
              columns={tableColumns}
              rows={displayTableRows}
              isLoading={tableLoading}
              monthFilter={{
                options: tableMonthOptions,
                selected: selectedTableMonth,
                onChange: setSelectedTableMonth,
              }}
            />
          </div>
        </div>
      </div>
      <DatePickerModal
        datePickerOpen={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        onApply={handleDateApply}
        initialStart={pickerInitialStart}
        initialEnd={pickerInitialEnd}
      />
      <DrillDownDrawer
        target={drillTarget}
        onClose={() => setDrillTarget(null)}
      />
    </LayoutContainer>
  );
};

export default ManagementDashboardDetails;
